import { useCallback, useMemo, useState } from "react";

import { ProjectIssueWithChildren } from "../types";

export interface IssueSelectionState {
  /** Set of currently selected issue IDs */
  selectedIssueIds: Set<string>;
  /** Total number of selected issues */
  selectedCount: number;
  /** Select or deselect a specific issue */
  onIssueSelection: (issueId: string, isSelected: boolean) => void;
  /** Clear all selections */
  onClearSelection: () => void;
  /** Toggle all issues in a given array (select all or deselect all) */
  onToggleAllIssues: (issues: ProjectIssueWithChildren[]) => void;
  /** Check if all issues in an array are selected */
  areAllIssuesSelected: (issues: ProjectIssueWithChildren[]) => boolean;
  /** Check if some (but not all) issues in an array are selected */
  areSomeIssuesSelected: (issues: ProjectIssueWithChildren[]) => boolean;
  /** Check if a specific issue is selected */
  isIssueSelected: (issueId: string) => boolean;
}

/**
 * Hook for managing issue selection state in the project management board.
 *
 * This hook provides a complete interface for handling issue selection,
 * including individual selection, bulk operations, and selection status queries.
 *
 * @param initialSelectedIds - Optional set of initially selected issue IDs
 * @returns IssueSelectionState object with selection state and methods
 */
export function useIssueSelection(
  initialSelectedIds: Set<string> = new Set()
): IssueSelectionState {
  const [selectedIssueIds, setSelectedIssueIds] =
    useState<Set<string>>(initialSelectedIds);

  const selectedCount = selectedIssueIds.size;

  const onIssueSelection = useCallback(
    (issueId: string, isSelected: boolean) => {
      setSelectedIssueIds((prev) => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(issueId);
        } else {
          newSet.delete(issueId);
        }
        return newSet;
      });
    },
    []
  );

  const onClearSelection = useCallback(() => {
    setSelectedIssueIds(new Set());
  }, []);

  const isIssueSelected = useCallback(
    (issueId: string) => selectedIssueIds.has(issueId),
    [selectedIssueIds]
  );

  const areAllIssuesSelected = useCallback(
    (issues: ProjectIssueWithChildren[]) => {
      return (
        issues.length > 0 &&
        issues.every((issue) => selectedIssueIds.has(issue.id))
      );
    },
    [selectedIssueIds]
  );

  const areSomeIssuesSelected = useCallback(
    (issues: ProjectIssueWithChildren[]) => {
      const hasSelected = issues.some((issue) =>
        selectedIssueIds.has(issue.id)
      );
      const hasAll = areAllIssuesSelected(issues);
      return hasSelected && !hasAll;
    },
    [selectedIssueIds, areAllIssuesSelected]
  );

  const onToggleAllIssues = useCallback(
    (issues: ProjectIssueWithChildren[]) => {
      const allSelected = areAllIssuesSelected(issues);

      setSelectedIssueIds((prev) => {
        const newSet = new Set(prev);

        if (allSelected) {
          // Deselect all issues in the array
          issues.forEach((issue) => newSet.delete(issue.id));
        } else {
          // Select all issues in the array
          issues.forEach((issue) => newSet.add(issue.id));
        }

        return newSet;
      });
    },
    [areAllIssuesSelected]
  );

  return useMemo(
    () => ({
      selectedIssueIds,
      selectedCount,
      onIssueSelection,
      onClearSelection,
      onToggleAllIssues,
      areAllIssuesSelected,
      areSomeIssuesSelected,
      isIssueSelected,
    }),
    [
      selectedIssueIds,
      selectedCount,
      onIssueSelection,
      onClearSelection,
      onToggleAllIssues,
      areAllIssuesSelected,
      areSomeIssuesSelected,
      isIssueSelected,
    ]
  );
}
